import { toast } from "sonner";
import { invokeProtectedFunction } from "@/lib/invokeProtectedFunction";
import { WORKSHOP_STORE_URL } from "@/components/landing/homepage-data";
import { closeCheckoutTab, navigateCheckoutTab, openCheckoutTab } from "@/lib/billing/checkoutTab";
import { checkoutAttribution, readFirstTouch } from "@/lib/tracking/firstTouch";
import type { PlanTier } from "@/lib/authz/plans";

/**
 * In-app checkout: asks the server to build a Shopify checkout for the given
 * tier (Seal selling plan applied) and redirects the browser there. Errors
 * surface as a toast. Only used for NET-NEW subscribers — existing subscribers
 * are routed to the customer portal instead (to avoid a second subscription).
 *
 * `email` (the signed-in user's account email) is forwarded so Shopify prefills
 * buyerIdentity and the resulting subscription auto-links to their account
 * instead of attaching to whatever address they happen to type.
 */
export async function startSubscriptionCheckout(
  tier: PlanTier,
  billing?: "annual" | "monthly",
  email?: string,
  countryCode?: string,
): Promise<boolean> {
  // Open the checkout tab up front (inside the click gesture) so the browser
  // allows it, then navigate it once the URL resolves.
  const checkoutTab = openCheckoutTab();
  // Loading feedback is the top progress bar (driven by the caller's pending
  // state), not a toast — so we only surface a toast on failure.
  try {
    const { data, error } = await invokeProtectedFunction<{ ok?: boolean; checkoutUrl?: string }>(
      "admin-create-subscription-checkout",
      { body: { tier, billing, email, countryCode, attribution: checkoutAttribution(readFirstTouch()) } },
    );
    if (error) throw error;
    if (!data?.ok || !data.checkoutUrl) {
      throw new Error("No checkout URL returned.");
    }
    navigateCheckoutTab(checkoutTab, data.checkoutUrl);
    return true;
  } catch (err) {
    closeCheckoutTab(checkoutTab);
    // Keep the technical error in telemetry; show the buyer a recoverable message
    // with a fallback to the hosted store page so a failed checkout never dead-ends.
    console.error("[checkout] failed to start:", err);
    toast.error("We couldn't start checkout. Please try again — or subscribe on our store page.", {
      action: {
        label: "Store page",
        onClick: () => window.open(WORKSHOP_STORE_URL, "_blank", "noopener,noreferrer"),
      },
    });
    return false;
  }
}
